import React, { useState, useEffect } from 'react'

interface IncomingCallNotificationProps {
  notification: {
    id: number
    title: string
    message: string
    metadata: {
      customer_name: string
      phone: string
      call_control_id: string
      customer_id?: number
    }
  }
  onAnswer: () => void
  onDecline: () => void
  onDismiss: () => void
}

export const IncomingCallNotification: React.FC<IncomingCallNotificationProps> = ({
  notification,
  onAnswer,
  onDecline,
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    // Auto-dismiss after 30 seconds if not answered
    const timer = setTimeout(() => {
      setIsVisible(false)
      onDismiss()
    }, 30000)

    return () => clearTimeout(timer)
  }, [onDismiss])

  if (!isVisible) return null

  return (
    <div className="fixed top-4 right-4 z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-green-500 p-6 min-w-[350px] animate-bounce">
      {/* Incoming Call Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            📞 Incoming Call
          </h3>
        </div>
        <button
          onClick={onDismiss}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        >
          ✕
        </button>
      </div>

      {/* Caller Information */}
      <div className="mb-6 text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-gray-800 to-gray-900 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="text-2xl">👤</span>
        </div>
        <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
          {notification.metadata.customer_name}
        </h4>
        <p className="text-gray-600 dark:text-gray-400">
          {notification.metadata.phone}
        </p>
      </div>

      {/* Call Actions */}
      <div className="flex space-x-4">
        <button
          onClick={onDecline}
          className="flex-1 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 active:scale-95"
        >
          📞❌ Decline
        </button>
        <button
          onClick={onAnswer}
          className="flex-1 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 active:scale-95"
        >
          📞✅ Answer
        </button>
      </div>

      {/* Call Timer */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          This notification will auto-dismiss in 30 seconds
        </p>
      </div>
    </div>
  )
} 